import { useMemo, useState } from 'react'
import { Alert, Button, Card, Form, Input, Modal, Progress, Select, Space, Table, Tabs, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime } from '@/shared/lib/format'
import { getCurrencyCode } from '@/shared/lib/format'
import { formatMoneyValue, type Money } from '@/shared/lib/money'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { PageHeader } from '@/shared/ui/PageHeader'
import { fetchAllUsers } from '@/features/identity/identityApi'
import {
  approveCollectRequest,
  fetchCollectCashiers,
  fetchCollectLimits,
  fetchCollectRequests,
  grantCollectCredit,
  rejectCollectRequest,
  type CollectLimit,
  type CollectRequest,
} from '@/features/collectLimit/collectLimitApi'

interface GrantValues {
  userId: string
  amount: Money | null
  reason?: string
}

interface DecisionValues {
  amount: Money | null
  note?: string
}

/** Xodimlarning pul yig'ish limiti (ADR-054): limit berish, so'rovlarni ko'rib chiqish. */
export function CollectLimitsPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { can } = usePermissions()
  const queryClient = useQueryClient()
  const currency = user?.tenant.currency ?? getCurrencyCode()
  const money = (amount: number | null | undefined) => (amount == null ? '—' : formatMoneyValue({ amount, currency }))

  const limits = useQuery({ queryKey: queryKeys.collectLimits.list, queryFn: ({ signal }) => fetchCollectLimits(signal) })
  const history = useQuery({
    queryKey: queryKeys.collectLimits.requests(null),
    queryFn: ({ signal }) => fetchCollectRequests(null, signal),
  })
  const cashiers = useQuery({ queryKey: queryKeys.collectLimits.cashiers, queryFn: ({ signal }) => fetchCollectCashiers(signal) })
  const users = useQuery({ queryKey: ['collectLimits', 'users'], queryFn: fetchAllUsers, enabled: can('user.read') })

  const [grantForm] = Form.useForm<GrantValues>()
  const [decisionForm] = Form.useForm<DecisionValues>()
  const [granting, setGranting] = useState<{ userId?: string } | null>(null)
  const [deciding, setDeciding] = useState<{ request: CollectRequest; approve: boolean } | null>(null)

  const people = useMemo(() => {
    const byId = new Map<string, string>()
    ;(limits.data ?? []).forEach((row) => byId.set(row.userId, row.userName))
    ;(cashiers.data ?? []).forEach((row) => byId.set(row.userId, row.userName))
    ;(users.data ?? []).forEach((row) => byId.set(row.id, row.username))
    if (user) byId.delete(user.id)
    return [...byId.entries()].map(([id, name]) => ({ value: id, label: name })).sort((a, b) => a.label.localeCompare(b.label))
  }, [limits.data, cashiers.data, users.data, user])

  const refresh = () => void queryClient.invalidateQueries({ queryKey: queryKeys.collectLimits.all })

  const grant = useMutation({
    mutationFn: (values: GrantValues) =>
      grantCollectCredit(
        values.userId,
        people.find((p) => p.value === values.userId)?.label ?? values.userId,
        values.amount?.amount ?? 0,
        values.reason?.trim() || null,
      ),
    onSuccess: () => {
      refresh()
      setGranting(null)
    },
  })
  const decide = useMutation({
    mutationFn: (values: DecisionValues) =>
      deciding?.approve
        ? approveCollectRequest(deciding.request.id, values.amount?.amount ?? null, values.note?.trim() || null)
        : rejectCollectRequest(deciding?.request.id ?? '', values.note?.trim() || null),
    onSuccess: () => {
      refresh()
      setDeciding(null)
    },
  })

  const openGrant = (userId?: string) => {
    grant.reset()
    setGranting(userId ? { userId } : {})
    grantForm.setFieldsValue({ userId, amount: null, reason: undefined })
  }
  const openDecision = (request: CollectRequest, approve: boolean) => {
    decide.reset()
    setDeciding({ request, approve })
    decisionForm.setFieldsValue({ amount: { amount: request.amount, currency }, note: undefined })
  }

  const pending = (limits.data ?? []).flatMap((row) => (row.pending ? [row.pending] : []))

  const limitColumns: ColumnsType<CollectLimit> = [
    { key: 'user', title: t('collectLimit.user'), render: (_, row) => row.userName },
    { key: 'limit', title: t('collectLimit.limit'), align: 'right', render: (_, row) => (row.limited ? money(row.limit) : '—') },
    { key: 'used', title: t('collectLimit.used'), align: 'right', render: (_, row) => (row.limited ? money(row.used) : '—') },
    {
      key: 'remaining',
      title: t('collectLimit.remaining'),
      width: 240,
      render: (_, row) =>
        row.limited ? (
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <span>{money(row.remaining)}</span>
            <Progress
              size="small"
              showInfo={false}
              percent={row.limit > 0 ? Math.min(100, Math.round((row.used / row.limit) * 100)) : 100}
              status={row.remaining <= 0 ? 'exception' : 'normal'}
            />
          </Space>
        ) : (
          <Typography.Text type="secondary">{t('collectLimit.noLimit')}</Typography.Text>
        ),
    },
    {
      key: 'pending',
      title: t('collectLimit.request'),
      render: (_, row) => (row.pending ? <Tag color="orange">{money(row.pending.amount)}</Tag> : null),
    },
    {
      key: 'actions',
      title: '',
      width: 150,
      render: (_, row) => (
        <Button size="small" onClick={() => openGrant(row.userId)}>
          {t('collectLimit.add')}
        </Button>
      ),
    },
  ]

  const requestColumns = (withActions: boolean): ColumnsType<CollectRequest> => [
    { key: 'user', title: t('collectLimit.user'), render: (_, row) => row.userName },
    { key: 'amount', title: t('collectLimit.amount'), align: 'right', render: (_, row) => money(row.amount) },
    { key: 'note', title: t('collectLimit.note'), render: (_, row) => row.note ?? '—' },
    { key: 'createdAt', title: t('collectLimit.requestedAt'), width: 170, render: (_, row) => formatDateTime(row.createdAt) },
    ...(withActions
      ? [
          {
            key: 'actions',
            title: '',
            width: 220,
            render: (_: unknown, row: CollectRequest) => (
              <Space>
                <Button size="small" type="primary" onClick={() => openDecision(row, true)}>
                  {t('collectLimit.approve')}
                </Button>
                <Button size="small" danger onClick={() => openDecision(row, false)}>
                  {t('collectLimit.reject')}
                </Button>
              </Space>
            ),
          },
        ]
      : [
          {
            key: 'state',
            title: t('collectLimit.state'),
            render: (_: unknown, row: CollectRequest) => (
              <Space direction="vertical" size={0}>
                <Tag color={row.state === 'APPROVED' ? 'green' : row.state === 'REJECTED' ? 'red' : 'orange'}>
                  {t(`collectLimit.states.${row.state}`)}
                </Tag>
                {row.approvedAmount != null ? <span>{money(row.approvedAmount)}</span> : null}
                {row.decidedByName ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {row.decidedByName}
                    {row.decisionNote ? ` · ${row.decisionNote}` : ''}
                  </Typography.Text>
                ) : null}
              </Space>
            ),
          },
        ]),
  ]

  const failure = limits.error ?? history.error

  return (
    <>
      <PageHeader
        title={t('nav.collectLimits')}
        description={t('pageIntro.collectLimits')}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openGrant()}>
            {t('collectLimit.grant')}
          </Button>
        }
      />
      {failure ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(failure, t)} /> : null}
      {pending.length > 0 ? (
        <Card title={t('collectLimit.pendingTitle', { count: pending.length })} style={{ marginBottom: 16 }}>
          <Table<CollectRequest> size="small" rowKey={(row) => row.id} columns={requestColumns(true)} dataSource={pending} pagination={false} />
        </Card>
      ) : null}
      <Tabs
        items={[
          {
            key: 'limits',
            label: t('collectLimit.limitsTab'),
            children: (
              <Table<CollectLimit>
                rowKey={(row) => row.userId}
                loading={limits.isPending}
                columns={limitColumns}
                dataSource={limits.data ?? []}
                pagination={false}
                locale={{ emptyText: t('collectLimit.empty') }}
              />
            ),
          },
          {
            key: 'history',
            label: t('collectLimit.historyTab'),
            children: (
              <Table<CollectRequest>
                rowKey={(row) => row.id}
                loading={history.isPending}
                columns={requestColumns(false)}
                dataSource={history.data ?? []}
                pagination={{ pageSize: 50 }}
              />
            ),
          },
        ]}
      />

      <Modal
        open={granting !== null}
        title={t('collectLimit.grantTitle')}
        okText={t('collectLimit.grant')}
        cancelText={t('common.cancel')}
        confirmLoading={grant.isPending}
        onCancel={() => {
          grant.reset()
          setGranting(null)
        }}
        onOk={() => submitOnValid(grantForm, (values) => grant.mutate(values))}
        destroyOnHidden
      >
        {grant.isError ? <Alert type="error" showIcon style={{ marginBottom: 12 }} message={translateError(grant.error, t)} /> : null}
        <Form form={grantForm} layout="vertical" preserve={false}>
          <Form.Item name="userId" label={t('collectLimit.user')} rules={[{ required: true, message: t('collectLimit.userRequired') }]}>
            <Select showSearch optionFilterProp="label" options={people} disabled={Boolean(granting?.userId)} />
          </Form.Item>
          <Form.Item name="amount" label={t('collectLimit.amount')} rules={[{ required: true, message: t('collectLimit.amountRequired') }]}>
            <MoneyInput currency={currency} minMinor={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="reason" label={t('collectLimit.note')}>
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={deciding !== null}
        title={deciding?.approve ? t('collectLimit.approveTitle') : t('collectLimit.rejectTitle')}
        okText={deciding?.approve ? t('collectLimit.approve') : t('collectLimit.reject')}
        okButtonProps={{ danger: !deciding?.approve }}
        cancelText={t('common.cancel')}
        confirmLoading={decide.isPending}
        onCancel={() => {
          decide.reset()
          setDeciding(null)
        }}
        onOk={() => submitOnValid(decisionForm, (values) => decide.mutate(values))}
        destroyOnHidden
      >
        {decide.isError ? <Alert type="error" showIcon style={{ marginBottom: 12 }} message={translateError(decide.error, t)} /> : null}
        {deciding ? (
          <Typography.Paragraph type="secondary">
            {t('collectLimit.decisionIntro', { user: deciding.request.userName, amount: money(deciding.request.amount) })}
          </Typography.Paragraph>
        ) : null}
        <Form form={decisionForm} layout="vertical" preserve={false}>
          {deciding?.approve ? (
            <Form.Item name="amount" label={t('collectLimit.approvedAmount')} rules={[{ required: true, message: t('collectLimit.amountRequired') }]}>
              <MoneyInput currency={currency} minMinor={1} style={{ width: '100%' }} />
            </Form.Item>
          ) : null}
          <Form.Item name="note" label={t('collectLimit.note')}>
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
